import { fromJS, Record } from 'immutable';
import { first, includes } from 'lodash';
import { Base } from './base';
import { logOut } from '../actions/auth';
import { uploadImagesToCloudinary } from '../actions/cloudinary';
import Contact from './contact';
import Place from './place';
import Image from './image';
import SupplyInvite from './supply-invite';
import SupplyNode from './supply-node';
import Post from './post';

import { Errors } from 'ui-utilities';

class Vendor extends Base(Record({
    id: null,
    user_id: null,
    price: '',
    cuisine: '',
    name: '',
    description: '',
    featured: false,
    short_description: '',
    plan: '',
    claimed: true,
    hidden: false,
    exploreProxies: [Contact, Place, Image, SupplyNode, Post],
    subscriberProxies: [Contact, Place, Image, SupplyInvite, SupplyNode, Post]
  })) {
    $create() {
      return this.api.post({
        resource: 'vendors'
      }, this.api.payload('vendors', this));
    }

    $get(id, filter = 'user_id', claimed = true) {
      const scope = this.state.auth
        .get('user')
        .get('scope');

      const proxies = scope !== 'merchant' ? this.exploreProxies : this.subscriberProxies;

      return this.api.get({
        resource: 'vendors?filter[' + filter + ']=' + id + '&filter[claimed]=' + claimed
      })
      .then(res => Promise.all(
        [res].concat(res.data.map(v => Promise.all(
          proxies.map(p => this.$proxy(new p({ vendor_id: v.id }), v.id))
        )))
      ))
      .then(res => this.dispatch(this.actions.receiveEntities(first(res).data, 'vendors')))
      .catch(err => new Errors(this.dispatch).async(err, logOut));
    }

    $delete() {
      return this.api.delete({
        id: this.id,
        resource: 'vendors'
      });
    }

    $getTags(id, action) {
      if (!id) id = this.id;

      if (!action) return false;

      return this.api.get({
        resource: 'vendors/' + id + '/relationships/tags'
      })
      .then(res => this.dispatch(action(res.data.map(t => t.id.toString()))));
    }

    $put() {
      if (this.state.entities.get('vendors').get(this.id).equals(this)) {
        return Promise.resolve({ data: this });
      }

      return this.api.put({
        id: this.id,
        resource: 'vendors'
      }, this.api.payload('vendors', this))
      .then(res => this.dispatch(this.actions.receiveEntity(res.data)))
      .catch(err => new Errors(this.dispatch).async(err, logOut));
    }

    $putTags(id, action) {
      if (!id) id = this.id;

      const { plan } = this.state;

      if (plan.get('editing_tags').size < 1) return Promise.resolve('No op');

      return this.api.patch({
        resource: 'vendors/' + id + '/relationships/tags'
      }, this.api.relationshipsPayload(plan.get('editing_tags').map(t => ({
        type: 'tags',
        id: t
      }))))
      .then(res => this.dispatch(action(plan.get('editing_tags'))))
      .catch(err => new Errors(this.dispatch).async(err, logOut));
    }

    $setProfileImage(image, resource = 'vendors', label = 'profile') {
      let existingProfilePic = this.profilePic(resource, label) && this.profilePic(resource, label).$delete();

      if (!existingProfilePic) existingProfilePic = Promise.resolve(null);

      return existingProfilePic
        .then(res => this.dispatch(uploadImagesToCloudinary(image)))
        .then(images => Promise.all(images.map(i => new Image({
            url: i,
            owner_id: resource === 'users' ? this.user_id : this.id,
            owner_type: resource,
            label: label
          }).$create()))
        );
    }

    $followers() {
      const resource = 'vendors/' + this.id + '/relationships/followers';

      return this.api.get({
        resource: resource
      });
    }

    places() {
      return this.state.entities.get('places').filter(p => p.vendor_id === this.id);
    }

    images() {
      return this.state.entities.get('images').filter(i => i.owner_id === this.id || i.owner_id === this.user_id);
    }

    tags() {
      const farmTags = [
        'general farm tags',
        'farm type'
      ];

      if (this.isFarm()) {
        return this.state.entities
          .get('tags')
          .filter(t => includes(farmTags, t.get('category').toLowerCase()));
      }
      return this.state.entities
        .get('tags')
        .filter(t => !includes(farmTags, t.get('category').toLowerCase()));
    }

    isFarm() {
      return this.plan === 'farm';
    }

    pendingInvites() {
      return this.state.entities.get('supplyInvites')
        .filter(s => s.get('invited_vendor_id') !== '' && s.get('vendor_id') === this.id && !s.get('accepted'));
    }

    invitesAwaitingApproval() {
      return this.state.entities.get('supplyInvites')
        .filter(s => s.get('invited_vendor_id') === this.id && !s.get('accepted'));
    }

    bannerImages() {
      const omit = fromJS(['logo', 'posts', 'profile', 'profile-wide']);

      return this.images().filterNot(i => omit.includes(i.get('label')));
    }

    profilePic(resource = 'vendors', label = 'profile') {
      return this.images().find(i => i.get('label') === label && i.get('owner_type') === resource);
    }

    profilePicUrl(transformations, resource = 'vendors') {
      let url = 'https://res.cloudinary.com/localvore-today/image/upload/v1493998098/profile.png';
      const pic = (this.places().first() && this.places().first().getProfileImage()) ||
        this.profilePic(resource) ||
        new Image({ url: url });

      return pic.withTransformations(transformations);
    }
  }

export default Vendor;



// WEBPACK FOOTER //
// ./src/records/vendor.js